import { useEffect } from 'react';
import { ArrowRight } from '@carbon/icons-react';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import { CarbonHeader } from './carbon-header.tsx';
import { type loader as rootLoader } from '../root.tsx';
import { NotifyProvider } from './notify.tsx';
import { useAnimatedNotification } from './animated-notification.tsx';

export function Forbidden() {
	const navigate = useNavigate();
	const rootData = useRouteLoaderData<typeof rootLoader>('root');
	const userPreference = rootData?.requestInfo?.userPrefs?.theme;

	const { showError, showSuccess, showInfo, showWarning } =
		useAnimatedNotification();

	// Set document title
	useEffect(() => {
		document.title = '403 - Access Denied | Bahandi';
	}, []);

	return (
		<NotifyProvider
			showSuccess={showSuccess}
			showError={showError}
			showInfo={showInfo}
			showWarning={showWarning}
		>
			<CarbonHeader
				userPreference={userPreference}
				showCalendarButton={true}
				showBellButton={true}
			/>
			{rootData?.isImpersonating && (
				<div className="fixed top-12 left-0 right-0 z-9999 bg-yellow-500 text-black px-4 py-2 text-center font-semibold flex items-center justify-center gap-4">
					<span>⚠️ You are currently viewing as {rootData.user?.nickname}</span>
					<Link
						to="/api/exit-impersonation"
						className="bg-black text-yellow-500 px-3 py-1 rounded text-sm font-bold hover:bg-gray-800 no-underline"
					>
						Exit Impersonation
					</Link>
				</div>
			)}
			<div className="min-h-screen bg-background flex items-center justify-center p-6 selection:bg-primary-base/20">
				{/* Bulletproof Flexbox Layout (Matches 404 exactly) */}
				<div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10 max-w-3xl animate-in fade-in duration-700">
					{/* Left: The Error Code */}
					<h1 className="text-7xl md:text-8xl font-black text-foreground tracking-tighter leading-none">
						403
					</h1>

					{/* Center: The Structural Divider (1px wide, 24 units high) */}
					<div className="hidden md:block w-px h-24 bg-border" />

					{/* Right: The Context & Actions */}
					<div className="flex flex-col items-center md:items-start text-center md:text-left">
						<h2 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-3">
							Access denied
						</h2>

						{/* The HR/Office Joke + Clear Explanation */}
						<p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md mb-8">
							Looks like your ID badge doesn't open this door. You don't have
							the required administrative permissions to view this area or
							record.
						</p>

						{/* High-End Typographic Actions */}
						<div className="flex items-center gap-6">
							<Link
								to="/me"
								className="text-sm font-bold text-primary-base hover:text-primary-hover transition-colors flex items-center gap-1.5 group"
							>
								Go to My Desk
								<ArrowRight
									size={16}
									className="group-hover:translate-x-1 transition-transform"
								/>
							</Link>

							<div className="w-1 h-1 rounded-full bg-border" />

							<button
								onClick={() => navigate(-1)}
								className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
							>
								Go back
							</button>
						</div>
					</div>
				</div>
			</div>
		</NotifyProvider>
	);
}
